import type { AdminQueryPort } from "../../contracts/admin-query-port";
import type {
  AdminDeviceKpi,
  AdminDeviceKpiPage,
  AdminDeviceKpiQuery,
  AdminQueryEnvelope,
} from "../../contracts/admin-read-models";
import { appendUserSameDeviceWorkspaceContext } from "../users/same-device-workspace-context";

export type AdminDeviceKpiDrilldownTarget = {
  failedFlows?: string;
  userSummary?: string;
  releaseRegression?: string;
};

function createQueryHref(pathname: string, key: string, value: string | undefined): string | undefined {
  if (value === undefined || value.length === 0) {
    return undefined;
  }

  const params = new URLSearchParams();
  params.set(key, value);
  return `${pathname}?${params.toString()}`;
}

function createReleaseRegressionHref(deviceKpi: AdminDeviceKpi): string | undefined {
  if (
    (deviceKpi.release === undefined || deviceKpi.release.length === 0) &&
    (deviceKpi.appVersion === undefined || deviceKpi.appVersion.length === 0)
  ) {
    return undefined;
  }

  const params = new URLSearchParams();

  if (deviceKpi.release) {
    params.set("release", deviceKpi.release);
  }

  if (deviceKpi.appVersion) {
    params.set("appVersion", deviceKpi.appVersion);
  }

  return `/admin/releases?${params.toString()}`;
}

function createUserSummaryHref(deviceKpi: AdminDeviceKpi): string | undefined {
  if (!deviceKpi.latestUserIdHash) {
    return undefined;
  }

  const params = appendUserSameDeviceWorkspaceContext(
    new URLSearchParams([["userIdHash", deviceKpi.latestUserIdHash]]),
    {
      deviceInstallId: deviceKpi.deviceInstallId,
      release: deviceKpi.release,
      appVersion: deviceKpi.appVersion,
      platform: deviceKpi.platform,
    },
  );

  return `/admin/users?${params.toString()}`;
}

export async function searchDeviceKpis(
  port: AdminQueryPort,
  query: AdminDeviceKpiQuery,
): Promise<AdminQueryEnvelope<AdminDeviceKpiPage>> {
  return port.searchDeviceKpis(query);
}

export function buildDeviceKpiDrilldownTarget(
  deviceKpi: AdminDeviceKpi,
): AdminDeviceKpiDrilldownTarget {
  return {
    failedFlows: createQueryHref("/admin/failed-flows", "deviceInstallId", deviceKpi.deviceInstallId),
    userSummary: createUserSummaryHref(deviceKpi),
    releaseRegression: createReleaseRegressionHref(deviceKpi),
  };
}
